import { c as _c } from "react/compiler-runtime";
import { ChartNoAxesCombined, Plus, SlidersHorizontal } from 'lucide-react';
import { useCallback } from 'react';
import { Switch } from '@/components/ui/switch';
import { useMeasureOptions, useMeasureOptionsActions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
interface OptionConsequence {
  icon: React.ComponentType<{
    className?: string;
  }>;
  label: string;
}
const Consequences = t0 => {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex flex-wrap gap-2 pt-1">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

/**
 * The two optional measurements.
 *
 * Each one says what it adds to the rest of the application, because turning
 * it on changes the measure table, the analysis and the design — and nothing
 * else on screen would say so.
 */
export const MeasureOptionsTab = () => {
  const $ = _c(97);
  const {
    t
  } = useTranslation();
  const {
    useManualSWRMeasurements,
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const {
    updateOption
  } = useMeasureOptionsActions();
  let t0;
  if ($[0] !== updateOption) {
    t0 = value => {
      updateOption("useManualSWRMeasurements", value);
    };
    $[0] = updateOption;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onUpdateUseManualSWRMeasurements = t0;
  let t1;
  if ($[2] !== updateOption) {
    t1 = value_0 => {
      updateOption("useSupportSpringMeasurements", value_0);
    };
    $[2] = updateOption;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onUpdateUseSpringSupportMeasurements = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("Two optional measurements, to turn on only if you take them on your piano.");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <p className="m-0 max-w-[70ch] text-[15px] text-muted-foreground">{t2}</p>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("Option {{number}} \xB7 Strike weight ratio", {
      number: 1
    });
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <span className="ptl-label">{t4}</span>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("Measure Strike Weight Ratio manually");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <h2 id="option-manual-swr" className="m-0 font-serif text-[22px] font-medium">{t6}</h2>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("By enabling this option, you will be able to manually measure the strike weight ratio of each key rather than using an automatically computed value.");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <p className="m-0 text-sm leading-[1.6] text-muted-foreground">{t8}</p>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("If a measured value is too far from the computed one, a warning will point out that the measurements are inconsistent.");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <p className="m-0 text-sm leading-[1.6] text-muted-foreground">{t10}</p>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t) {
    t12 = t("\u201CSWR\u201D column in Measure");
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = {
      icon: Plus,
      label: t12
    };
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("Measured and computed in Analyze");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = {
      icon: ChartNoAxesCombined,
      label: t14
    };
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t13 || $[33] !== t15) {
    t16 = <Consequences items={[t13, t15]} />;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t11 || $[36] !== t16 || $[37] !== t5 || $[38] !== t7 || $[39] !== t9) {
    t17 = <div className="flex flex-[1_1_420px] flex-col gap-2.5">{t5}{t7}{t9}{t11}{t16}</div>;
    $[35] = t11;
    $[36] = t16;
    $[37] = t5;
    $[38] = t7;
    $[39] = t9;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== onUpdateUseManualSWRMeasurements || $[42] !== useManualSWRMeasurements) {
    t18 = <Switch className="cursor-pointer" aria-labelledby="option-manual-swr" checked={useManualSWRMeasurements} onCheckedChange={onUpdateUseManualSWRMeasurements} />;
    $[41] = onUpdateUseManualSWRMeasurements;
    $[42] = useManualSWRMeasurements;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t17 || $[45] !== t18) {
    t19 = <div className="flex flex-wrap items-start gap-6 border-b border-border px-6 py-[22px]">{t17}{t18}</div>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t) {
    t20 = t("Option {{number}} \xB7 Wippen support springs", {
      number: 2
    });
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t20) {
    t21 = <span className="ptl-label">{t20}</span>;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t) {
    t22 = t("Measure Wippen Support Spring tension");
    $[51] = t;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22) {
    t23 = <h2 id="option-support-springs" className="m-0 font-serif text-[22px] font-medium">{t22}</h2>;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t) {
    t24 = t("With this option on, an extra field appears to measure the down weight of each key with the support spring engaged. Those values feed the spring tension calculation and bring up a dedicated chart in the analysis.");
    $[55] = t;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t24) {
    t25 = <p className="m-0 text-sm leading-[1.6] text-muted-foreground">{t24}</p>;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t) {
    t26 = t("This measurement is optional, even if you plan to use support springs in your design. It simply records the condition they are in today.");
    $[59] = t;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t26) {
    t27 = <p className="m-0 text-sm leading-[1.6] text-muted-foreground">{t26}</p>;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t) {
    t28 = t("\u201CD with spring\u201D column in Measure");
    $[63] = t;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t28) {
    t29 = {
      icon: Plus,
      label: t28
    };
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t) {
    t30 = t("Analyze \u203A Wippen support springs");
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t30) {
    t31 = {
      icon: ChartNoAxesCombined,
      label: t30
    };
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t) {
    t32 = t("\u201CKeep as measured\u201D mode in Design");
    $[71] = t;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t32) {
    t33 = {
      icon: SlidersHorizontal,
      label: t32
    };
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t29 || $[76] !== t31 || $[77] !== t33) {
    t34 = <Consequences items={[t29, t31, t33]} />;
    $[75] = t29;
    $[76] = t31;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t21 || $[80] !== t23 || $[81] !== t25 || $[82] !== t27 || $[83] !== t34) {
    t35 = <div className="flex flex-[1_1_420px] flex-col gap-2.5">{t21}{t23}{t25}{t27}{t34}</div>;
    $[79] = t21;
    $[80] = t23;
    $[81] = t25;
    $[82] = t27;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== onUpdateUseSpringSupportMeasurements || $[86] !== useSupportSpringMeasurements) {
    t36 = <Switch className="cursor-pointer" aria-labelledby="option-support-springs" checked={useSupportSpringMeasurements} onCheckedChange={onUpdateUseSpringSupportMeasurements} />;
    $[85] = onUpdateUseSpringSupportMeasurements;
    $[86] = useSupportSpringMeasurements;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== t35 || $[89] !== t36) {
    t37 = <div className="flex flex-wrap items-start gap-6 px-6 py-[22px]">{t35}{t36}</div>;
    $[88] = t35;
    $[89] = t36;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== t19 || $[92] !== t37) {
    t38 = <section className="rounded-md border border-border bg-card">{t19}{t37}</section>;
    $[91] = t19;
    $[92] = t37;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  let t39;
  if ($[94] !== t3 || $[95] !== t38) {
    t39 = <div className="flex flex-col gap-6">{t3}{t38}</div>;
    $[94] = t3;
    $[95] = t38;
    $[96] = t39;
  } else {
    t39 = $[96];
  }
  return t39;
};
function _temp(item) {
  return <span key={item.label} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-[13px] text-muted-foreground"><item.icon className="size-3.5" />{item.label}</span>;
}